import React, { useCallback, useEffect, useState } from "react";

import { Link, useHistory } from "react-router-dom";
import Select from "react-select";
import Swal from "sweetalert2";

import { useDidMountEffect } from "../../../../../hooks/useDidMountEffect";

import { BrComerceApiService } from "../../../../../services/BrComerceApiService";
import debounce from "../../../../../utils/debounce";
import InputText from "../../../../../components/InputText";
import Loading from "../../../../home/components/Loading";
import Container from "../../../components/Container";
import { StyledTooltip } from "../../../components/Tooltip";
import store from "../../../../../store/store";
import Switch from "react-switch";

import styles from "./driversListing.module.scss";
import { FaCirclePlus } from "react-icons/fa6";
import {
  FiEdit3,
  FiChevronLeft,
  FiChevronRight
} from "react-icons/fi";
import { selectStyle } from "../../../../../components/InputSelectAsync";
import { formatPhone } from "../../../../../utils/FormatPhone";

const paginationSelectStyle = {
  control: styles => ({
    ...styles,
    backgroundColor: "inherit",
    width: "100%",
    border: "none",
    boxShadow: "none",
    outline: "none",
    color: "#807E80",
    padding: "0",
    "&:hover": {
      borderColor: "none"
    },
    maxHeight: "48px",
    cursor: "pointer"
  }),
  container: styles => ({
    ...styles,
    minWidth: "56px",
    outline: "none",
    borderBottom: "1px solid #4D4D4D",
    padding: "0",
    cursor: "pointer"
  }),
  indicatorSeparator: styles => ({
    ...styles,
    display: "none",
    border: "none"
  }),
  option: (styles, { isSelected }) => ({
    ...styles,
    fontSize: "14px",
    fontFamily: "Mulish",
    fontWeight: 400,
    color: isSelected ? "#fff" : "#807E80",
    "&:hover": {
      backgroundColor: "#c1c1c1",
      color: "#color: #333333;",
      cursor: "pointer"
    },

    backgroundColor: isSelected ? "#409e6a" : "#fff"
  }),
  valueContainer: styles => ({
    ...styles,
    padding: "0"
  }),
  dropdownIndicator: styles => ({
    ...styles,
    padding: "0",
    color: "#4D4D4D"
  })
};

const activeOptions = [
  {
    value: true,
    label: "Ativos"
  },
  {
    value: false,
    label: "Desativados"
  },
]

const DriversListing = () => {
  const { auth } = store.getState();
  const { user } = auth;

  const api = new BrComerceApiService();
  const history = useHistory();

  const [isLoading, setIsLoading] = useState(false);
  const [actualPage, setActualPage] = useState(1);
  const [totalPages, setTotalPages] = useState(0);
  const [resultsPages, setResultsPages] = useState({ label: "10", value: 10 });

  const [drivers, setDrivers] = useState([]);
  const [searchedName, setSearchedName] = useState("");
  const [bringActives, setBringActives] = useState(activeOptions[0]);

  const loadDrivers = useCallback(
    debounce(async params => {
      try {
        setIsLoading(true);
        const response = await api.makeHttpRequest({
          url: "/driver-admin",
          params: {
            page: params?.page ?? 1,
            resultsPerPage: params?.resultsPages ?? 10,
            ...(params?.text && { text: params.text }),
            ...((params?.active !== undefined) && { active: params.active }),
          }
        });

        setActualPage(response.actualPage);
        setTotalPages(response.nPages);
        setDrivers(response.results);
      } catch (e) {
        console.log("🚀 ~ file: index.jsx ~ line 58 ~ UserListing ~ e", e);
        Swal.fire({
          text: "Erro ao carregar lista de usuários",
          title: "Usuários",
          icon: "error"
        });
      } finally {
        setIsLoading(false);
      }
    }, 500),
    [drivers]
  );

  function removeCharactersAndSpaces(str) {
    const hasAlphabet = /[a-zA-Z]/.test(str);
    if (hasAlphabet) {
      return str;
    }

    // Remove "(", ")", "-", e espaços
    return str.replace(/[()\-\s]/g, '');
  }

  useDidMountEffect(() => {
    loadDrivers({
      page: actualPage,
      text: removeCharactersAndSpaces(searchedName),
      active: bringActives?.value,
      resultsPages: resultsPages.value
    });
  }, [actualPage, resultsPages, searchedName, bringActives]);

  useEffect(() => {
    loadDrivers({active: bringActives?.value});
  }, []);

  useEffect(() => {
    window.setPageTitle("Motoristas");
    if (user.profile !== "admin") {
      history.push("/");
    }
  }, []);

  const handleOnConfirmDesactive = async (id) => {
    setIsLoading(true);
    await api
      .makeHttpRequest({
        method: "PUT",
        url: `/driver-admin/status/${id}`,
      })
      .then(response => {
        loadDrivers({
          page: actualPage,
          text: removeCharactersAndSpaces(searchedName),
          active: bringActives?.value,
          resultsPages: resultsPages.value
        });
        Swal.fire({
          title: "Sucesso!",
          text: `O motorista foi ${response?.active ? "ativado" : "desativado"} com sucesso.`,
          icon: "success",
          showCancelButton: false,
          confirmButtonText: "Ok"
        });
        // history.push("/motoristas");
      })
      .catch(e => {
        Swal.fire({
          icon: "error",
          title: "Ocorreu um erro!",
          text: "Não foi possível fazer a desativação do usuário."
        });
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  const handleDesactiveDriver = async (id, driverIsActive) => {
    if (!id || driverIsActive === undefined) return
    Swal.fire({
      title: "Você tem certeza?",
      html:
        driverIsActive ? "Deseja prosseguir com a desativação do usuário?" : "Deseja ativar o usuário?",
      icon: "warning",
      cancelButtonText: "Cancelar",
      showCancelButton: true,
      confirmButtonText: driverIsActive ? "Sim, desejo desativá-lo!" : "Sim, desejo ativá-lo!"
    }).then(response => {
      if (response?.value) {
        handleOnConfirmDesactive(id);
      }
    });
  };

  const onPage = newPage => {
    setActualPage(newPage - 1);
  };

  const onNext = () => {
    setIsLoading(true);
    onPage(actualPage + 1);
    setActualPage(actualPage + 1);
  };

  const onPrevious = () => {
    setIsLoading(true);
    onPage(actualPage - 1);
    setActualPage(actualPage - 1);
  };

  const perPageOptions = [
    {
      label: "10",
      value: 10
    },
    {
      label: "15",
      value: 15
    },
    {
      label: "20",
      value: 20
    },
    {
      label: "50",
      value: 50
    },
    {
      label: "100",
      value: 100
    }
  ];

  return (
    <>
      <Loading isLoading={isLoading} />
      <Container title="Motoristas">
        <div className={styles.contentBody}>
          <div className={styles.filters}>
            <div className={styles.inputSearch}>
              <InputText
                id="Buscar"
                value={searchedName}
                onChange={e => setSearchedName(e.target.value)}
                renderIcon={null}
                placeholder="Buscar por nome ou telefone"
              />
            </div>

            <div className={styles.activeSelect}>
              <Select
                onChange={(value) => setBringActives(value)}
                isClearable
                options={activeOptions}
                name="status"
                value={bringActives}
                placeholder="filtrar por status"
                styles={selectStyle}
              />
            </div>

            <div className={styles.containerBtnAddUser}>
              <Link to="/motoristas/cadastrar">
                <button type="button" className={styles.btnAddUser}>
                  <FaCirclePlus fill="#fff" color="#fff" />
                  <span>Adicionar novo motorista</span>
                </button>
              </Link>
            </div>
          </div>
          <div className={styles.contentTable}>
            <table className={styles.tableContainer}>
              <thead>
                <tr>
                  <th width="50%">Nome</th>
                  <th width="45%">Telefone</th>
                  <th width="5%">Ações</th>
                </tr>
              </thead>
              <tbody>
                {!!drivers.length &&
                  drivers.map(driver => {
                    return (
                      <tr key={driver?.id}>
                        <td>{driver?.name ?? "--"}</td>
                        <td>
                          {formatPhone(driver?.phone)}
                        </td>
                        <td className={styles.tooltipWrapper}>
                          <StyledTooltip text="Editar" position="top" arrow>
                            <div>
                              <FiEdit3
                                onClick={() =>
                                  history.push(`/motoristas/${driver.id}/editar`)
                                }
                                style={{
                                  cursor: "pointer",
                                  marginRight: "5px"
                                }}
                                size={20}
                                color="#409E6A"
                              />
                            </div>
                          </StyledTooltip>
                          <StyledTooltip text={driver?.active ? "Desativar" : "Ativar"} position="top" arrow>
                            <div>
                              <Switch
                                checked={driver?.active}
                                onChange={() =>
                                  handleDesactiveDriver(driver?.id, driver?.active)
                                }
                                offColor="#B3B3B3"
                                onColor="#409E6A"
                                offHandleColor="#FFF"
                                onHandleColor="#FFF"
                                uncheckedIcon={false}
                                checkedIcon={false}
                                width={26}
                                height={13}
                                handleDiameter={9}
                              />
                            </div>
                          </StyledTooltip>
                          {/* <StyledTooltip text="Deletar" position="top" arrow>
                            <div>
                              <FiTrash2
                                onClick={() => handleDeleteUser(driver.id)}
                                style={{
                                  cursor: "pointer"
                                }}
                                size={20}
                                color="#409E6A"
                              />
                            </div>
                          </StyledTooltip> */}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
          <div className={styles.paginationContainer}>
            <div className={styles.perPageContent}>
              <span>Itens por página:</span>
              <Select
                styles={paginationSelectStyle}
                value={resultsPages}
                options={perPageOptions}
                onChange={e => setResultsPages(e)}
                menuPlacement="top"
              />
            </div>
            <h6>
              {actualPage} - {resultsPages.value} de {totalPages}
            </h6>
            <div className={styles.controlPage}>
              <FiChevronLeft
                size={25}
                onClick={actualPage > 1 ? onPrevious : ""}
                color={actualPage > 1 ? "#409E6A" : "#2D724C"}
                className="arrow"
              />

              <FiChevronRight
                size={25}
                onClick={actualPage < totalPages ? onNext : ""}
                color={actualPage < totalPages ? "#409E6A" : "#2D724C"}
                className="arrow"
              />
            </div>
          </div>
        </div>
      </Container>
    </>
  );
};

export { DriversListing };
